/* eslint-disable no-nested-ternary */
/* eslint-disable react/prop-types */
import React, { useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { BsArrowUp } from "react-icons/bs";
import { useRouteMatch } from "react-router-dom";
import {
  Box,
  Collapse,
  IconButton,
  Typography,
  Tooltip,
  makeStyles,
} from "@material-ui/core";
import ScaleLoader from "react-spinners/ScaleLoader";
import {
  computeEstimationTotals,
  groupEstimationsByDate,
} from "lib/utils/estimations";
import getElasticities from "lib/requests/elasticities";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import LockIcon from "@material-ui/icons/Lock";
import athenaClient from "lib/axios/clients";
import { groupByPriceAndReduceEstimations } from "lib/filters/estimations";
import {
  REACT_APP_BASE_URL,
  primaryColor,
  PriceOut,
} from "resources/constants";
import buildRequestUrl from "lib/requests/requestUtils";
import {
  filterEstimationFieldsWithoutIdentifier,
  getEstimationsByCycle,
} from "lib/requests/estimations";
import useDidMountEffect from "lib/react/customHooks";
import _ from "lodash";
import {
  setScenarioEstimations,
  setSelectedScenario,
  setCacheReferenceScenario,
  setCacheScenario,
  setReferenceScenario,
  addScenarioTotals,
} from "app/scenarios/duck/slices";
import CardComponent from "app/common/CardComponent";
import { handleScenarioType } from "lib/utils/scenarios";
import mixpanelTrack from "app/common/MixpanelComponent";
import { getFormattedCellValue, nFormatter, getROI } from "lib/utils";
import BadRequestDialog from "app/common/dialog/BadRequestDialog";
import ElasticityCurveChartComponent from "./ElasticityCurveChartComponent";

const useRowStyles = makeStyles((theme) => ({
  row: {
    display: "flex",
    justifyContent: "space-evenly",
    backgroundColor: "#fcfcfc",
    "&:hover": {
      cursor: "pointer",
      backgroundColor: "#ECEFF1",
    },
  },
  rowCell: {
    padding: "10px 5px 10px 5px",
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    height: "60px",
    "&:first-child": {
      flexBasis: "50px",
      maxWidth: "50px",
      minWidth: "50px",
    },
    width: "145px",
    minWidth: "145px",
  },
  large: {
    wordBreak: "break-all",
    width: "145px",
    minWidth: "145px",
  },
  cellValue: {
    display: "-webkit-box",
    overflow: "hidden",
    textOverflow: "ellipsis",
    "-webkit-line-clamp": "2",
    "-webkit-box-orient": "vertical",
    [theme.breakpoints.up("xs")]: {
      fontSize: "0.750rem",
    },
    [theme.breakpoints.up("sm")]: {
      fontSize: "0.750rem",
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "0.750rem",
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "0.800rem",
    },
    [theme.breakpoints.up("xl")]: {
      fontSize: "0.800rem",
    },
  },
  numeric: {
    alignItems: "flex-end",
  },
  expansionDetailsContainer: {
    // Height of the row render in EstimationComponent
    height: "360px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    // padding: theme.spacing(2),
  },
  light: {
    color: "#9e9e9e",
  },
  cellPercentage: {
    marginLeft: "5px",
    display: "flex",
    alignItems: "center",
    fontSize: "12px",
  },
  darkGreyBackground: {
    backgroundColor: "rgba(221, 221, 221, 0.2)",
  },
  expandButton: {
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  titleCardContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingTop: "25px",
  },
  titleCard: {
    fontWeight: "bold",
    fontSize: 22,
    color: "#666666",
  },
  textCardContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: "5px",
    color: "#999999",
  },
  textOwnElasticity: {
    fontWeight: "bold",
  },
  itemContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
  },
  priceSliderContainer: {
    width: "10%",
    alignSelf: "center",
    marginBottom: "10px",
    marginRight: "5px",
  },
  priceSliderContainerTypeOne: {
    width: "90%",
  },
  priceSliderContainerTypeTwo: {
    width: "90%",
    marginRight: "5px",
  },
  priceSliderContainerTypeThree: {
    width: "90%",
  },
  textSlider: {
    alignSelf: "center",
    fontWeight: "bold",
    fontSize: 18,
    color: "#999999",
    display: "flex",
    justifyContent: "center",
  },
}));

export default function EstimationRowComponent({
  row,
  referenceRow,
  isReference,
  isTradePromotionAnnual,
  style,
  index,
  groupBy,
  setSelectedIndex,
  expandedRow,
  setExpandedRow,
  isLoading,
  setIsLoading,
  isBlocked,
}) {
  const { url } = useRouteMatch();
  const isTpo = !url.includes("pricing");
  const labelTableColumns = ["product", "retailer", "area"];
  const numericTableColumns = isTpo
    ? ["price", "units", "sales", "margin", "units_in", "investment_in"]
    : ["price", "units", "sales", "margin", "units_in", "sales_in"];
  const classes = useRowStyles();
  const dispatch = useDispatch();
  const [currentTupleData, setCurrentTupleData] = useState(null);
  const [selectedPrice, setSelectedPrice] = useState();
  const scenariosSlice = useSelector((state) => state.scenarios);
  const metadataSlice = useSelector((state) => state.metadata);
  const [ownElasticity, setOwnElasticity] = useState(null);
  const [isBadRequest, setIsBadRequest] = useState(false);

  const configCatSlice = useSelector((state) => state.auth.configCatUser);
  const priceOutLabelByTenant = configCatSlice?.features.isUsaTerminology
    ? PriceOut
    : "Price Out";
  // We use this variable to avoid having to use the ternary expression inside useState everywhere we want to paint rowValues. To avoid extra requests to the server, every time an estimation changes we will update the value of selected estimation so we do not have to ask the server for the new value after the update
  const [rowValues, setRowValues] = useState(row);

  useDidMountEffect(async () => {
    try {
      await setIsLoading(true);
      const estimationsForPrice = await getEstimationsByCycle({
        filters: {
          product: row.product,
          ...(row.retailer && { retailer: row.retailer }),
          ...(row.retailer_group && { retailer_group: row.retailer_group }),
          ...(row.area && { area: row.area }),
          cycle: { id: scenariosSlice.selectedScenario.scenario.cycle_id },
          data_source_id: 1,
          min_price: selectedPrice - 0.001,
          max_price: selectedPrice + 0.001,
        },
      });

      const updatedScenarioEstimations = {};

      // For each tuple involved in the price change we want to find the current scenario estimation for that tuple and update it with the values for the new price
      const scenarioEstimationInEstimationBycycle = [];
      _.forEach(estimationsForPrice.data, (estimation) => {
        const estimationKey = `${estimation.product_id}-${estimation.area_id}-${estimation.retailer_id}`;
        if (
          scenariosSlice.selectedScenario.estimations[estimationKey] !==
          undefined
        ) {
          scenarioEstimationInEstimationBycycle.push(estimation);
        }
      });
      const scenarioEstimationsToUpdate = _.map(
        scenarioEstimationInEstimationBycycle,
        (estimation) => {
          const estimationKey = `${estimation.product_id}-${estimation.area_id}-${estimation.retailer_id}`;
          const targetScenarioEstimation =
            scenariosSlice.selectedScenario.estimations[estimationKey];

          const targetReferenceEstimation =
            scenariosSlice.referenceScenario.estimations[estimationKey];

          const newEstimationValues = filterEstimationFieldsWithoutIdentifier(
            estimation,
            targetReferenceEstimation,
            configCatSlice?.features.newInvestmentCalculation
          );

          // The reason why we are creating an object in addition to the array returned by the map is that the estimations stored in the object contain expanded properties that are used by the client application while the array resulting from the map have the bare minimum properties required to perform updates in the server side
          updatedScenarioEstimations[estimationKey] = {
            ...targetScenarioEstimation,
            ...newEstimationValues,
          };

          return {
            id: targetScenarioEstimation.id,
            scenario_id: targetScenarioEstimation.scenario_id,
            estimation_cycle_id: estimation.id,
            ...newEstimationValues,
          };
        }
      );

      const keyToUpdate = `${rowValues.product_id}${
        row.area ? "-".concat(rowValues.area_id) : ""
      }${row.retailer ? "-".concat(rowValues.retailer_id) : ""}${
        row.retailer_group ? "-".concat(rowValues.retailer_group_id) : ""
      }`;

      const EstimationsUpdated = {
        estimations: {
          ...scenariosSlice.selectedScenario.estimations,
          ...updatedScenarioEstimations,
        },
      };

      let rowValueUpdate;

      if (groupBy) {
        const groupedValues = _.groupBy(
          EstimationsUpdated.estimations,
          (estimation) => {
            const key = `${estimation.product_id}${
              row.area ? "-".concat(estimation.area_id) : ""
            }${row.retailer ? "-".concat(estimation.retailer_id) : ""}${
              row.retailer_group
                ? "-".concat(estimation.retailer.retailer_group_id)
                : ""
            }`;
            return key;
          }
        );

        rowValueUpdate = groupEstimationsByDate(
          groupedValues,
          metadataSlice["custom-projection"],
          scenariosSlice.referenceScenario.estimations,
          configCatSlice?.features.newInvestmentCalculation
        )[keyToUpdate];
      } else rowValueUpdate = EstimationsUpdated.estimations[keyToUpdate];

      setRowValues({
        ...rowValues,
        price: rowValueUpdate.price,
        price_in: rowValueUpdate.price_in,
        units: rowValueUpdate.units,
        units_in: rowValueUpdate.units_in,
        sales: rowValueUpdate.sales,
        sales_in: rowValueUpdate.sales_in,
        margin: rowValueUpdate.margin,
        investment: rowValueUpdate.investment,
        investment_in: rowValueUpdate.investment_in,
        roi: getROI(
          parseInt(rowValueUpdate.margin.toFixed(0), 10),
          referenceRow.margin,
          parseInt(
            (
              (referenceRow.price_in - rowValueUpdate.price_in) *
              rowValueUpdate.units_in
            ).toFixed(0),
            10
          ),
          {
            strategy: configCatSlice?.features.roiStrategy,
            unitsEsc: rowValueUpdate.units,
            unitsRef: referenceRow.units,
          }
        ),
      });
      // We update the scenario estimations
      const newTotals = {
        [scenariosSlice.selectedScenario.scenario.id]: {
          ...scenariosSlice.selectedScenario,
          ...computeEstimationTotals(
            {
              ...scenariosSlice.selectedScenario.estimations,
              ...updatedScenarioEstimations,
            },
            scenariosSlice.referenceScenario.estimations,
            configCatSlice?.features.newInvestmentCalculation,
            configCatSlice?.features.roiStrategy
          ),
        },
      };
      dispatch(addScenarioTotals(newTotals));
      dispatch(setSelectedScenario(EstimationsUpdated));

      dispatch(
        setCacheScenario({
          [handleScenarioType(
            scenariosSlice.selectedScenario.scenario.cycle.cycle_type_id
          )]: {
            ...scenariosSlice.selectedScenario,
            ...EstimationsUpdated,
          },
        })
      );

      if (
        scenariosSlice.selectedScenario.scenario.id ===
        scenariosSlice.referenceScenario.scenario.id
      ) {
        const EstimationsReferenceUpdated = {
          estimations: {
            ...scenariosSlice.referenceScenario.estimations,
            ...updatedScenarioEstimations,
          },
        };
        dispatch(setReferenceScenario(EstimationsReferenceUpdated));
        dispatch(
          setCacheReferenceScenario({
            [handleScenarioType(
              scenariosSlice.referenceScenario.scenario.cycle.cycle_type_id
            )]: {
              ...scenariosSlice.referenceScenario,
              ...EstimationsReferenceUpdated,
            },
          })
        );
      }
      dispatch(
        setScenarioEstimations({
          id: scenariosSlice.selectedScenario.scenario.id,
          estimations: {
            ...scenariosSlice.selectedScenario.estimations,
            ...updatedScenarioEstimations,
          },
        })
      );

      // Finally we request the server to update the scenario estimations in the database
      const config = {
        method: "PATCH",
        url: `${REACT_APP_BASE_URL}/scenario_estimations`,
        data: scenarioEstimationsToUpdate,
      };
      athenaClient(config);
    } catch (error) {
      setIsBadRequest(true);
    } finally {
      await setIsLoading(false);
    }
  }, [selectedPrice]);

  const comprobeInvestmentPositive = (rowToComprobe, referenceRowPrice) => {
    const newRow = {
      ...rowToComprobe,
      investment_in: 0,
    };

    if (rowToComprobe.price >= referenceRowPrice && !groupBy) {
      setRowValues(newRow);
    } else {
      setRowValues(rowToComprobe);
    }
  };

  useMemo(() => {
    comprobeInvestmentPositive(row, referenceRow.price);
  }, [row]);

  const handleOnRowClick = async () => {
    if (isBlocked) return;
    if (index === expandedRow) {
      setExpandedRow(-1);
    } else if (!isLoading) {
      try {
        setCurrentTupleData(null);
        /* MIXPANEL TRACKS */
        mixpanelTrack("Expand estimation row", {
          ...(row.product && { product: row.product.label }),
          ...(row.retailer && { retailer: row.retailer.label }),
          ...(row.area && { area: row.area.label }),
          cycle: scenariosSlice.selectedScenario.scenario.cycle.label,
        });
        /* END OF MIXPANEL TRACKS */

        setSelectedIndex(index);
        setExpandedRow(index);

        const tupleRequestParams = {
          filters: {
            product: row.product,
            ...(row.retailer && { retailer: row.retailer }),
            ...(row.retailer_group && { retailer_group: row.retailer_group }),
            ...(row.area && { area: row.area }),
            cycle: { id: scenariosSlice.selectedScenario.scenario.cycle_id },
            data_source_id: 1,
          },
        };

        const estimationsUrl = buildRequestUrl(
          {
            ...tupleRequestParams,
            group_by: ["price"],
            sum: ["units", "margin", "units_in", "sales"],
            avg_weight_by_units_in: ["price_in", "price_in_wo_taxes"],
            avg_weight_by_units: ["price_out_wo_taxes"],
          },
          "estimations_by_cycle"
        );

        const estimationsConfig = {
          method: "GET",
          url: estimationsUrl,
        };

        setOwnElasticity(
          _.round(
            (
              await getElasticities({
                product_id: row.product_id,
                source_product_id: row.product_id,
                data_source_id: 1,
                is_product_client: 1,
                ...(row.retailer && { retailer_id: row.retailer_id }),
                ...(row.retailer_group && {
                  retailer_group_id: row.retailer_group.id,
                }),
                ...(row.area && { area_id: row.area_id }),
                avg_weight_by_pred_units_last_price: ["elasticity"],
                group_by: "product_id",
                filter_type: "same",
                min_date:
                  scenariosSlice.selectedScenario.scenario.cycle.initial_date,
                max_date:
                  scenariosSlice.selectedScenario.scenario.cycle.final_date,
              })
            ).data[0]?.avg_weight_by_pred_units_last_price_elasticity,
            2
          )
        );

        const [estimationsResponse] = await Promise.all([
          athenaClient(estimationsConfig),
        ]);

        setCurrentTupleData({
          estimations: groupByPriceAndReduceEstimations(
            estimationsResponse.data.map((obj) => {
              // Destructure the object to extract the old key value
              // eslint-disable-next-line camelcase
              const { avg_weight_by_units_in_price_in, ...rest } = obj;
              // Create a new object with the renamed key and the rest of the properties
              return { price_in: avg_weight_by_units_in_price_in, ...rest };
            }),
            metadataSlice["custom-projection"],
            "sum_"
          ),
        });
      } catch (error) {
        setIsBadRequest(true);
      }
    }
  };

  return (
    <div style={style}>
      <BadRequestDialog open={isBadRequest} />
      <div
        role="presentation"
        onClick={() => handleOnRowClick()}
        className={`${classes.row} ${index % 2 && classes.darkGreyBackground} ${
          rowValues.estimation_cycle_id === referenceRow.estimation_cycle_id &&
          !isReference &&
          !groupBy &&
          !isTradePromotionAnnual
            ? classes.light
            : null
        }`}
        style={{ cursor: isBlocked ? "default" : "pointer" }}
      >
        <div className={classes.rowCell}>
          <IconButton
            aria-label="expand row"
            size="small"
            classes={{
              root: classes.expandButton,
            }}
            disableRipple
            style={{ cursor: isBlocked ? "default" : "pointer" }}
          >
            {isBlocked ? (
              <LockIcon fontSize="small" />
            ) : index === expandedRow ? (
              <KeyboardArrowUpIcon />
            ) : (
              <KeyboardArrowDownIcon />
            )}
          </IconButton>
        </div>
        {_.map(labelTableColumns, (columnName) => {
          if (groupBy && ["retailer", "area"].includes(columnName)) return null;
          return (
            <div
              className={`${classes.rowCell} ${classes.large}`}
              key={columnName}
            >
              <Tooltip
                title={rowValues[columnName].label}
                placement="top-start"
              >
                <Typography variant="body2" className={classes.cellValue}>
                  {rowValues[columnName].label}
                </Typography>
              </Tooltip>
            </div>
          );
        })}

        {_.map(numericTableColumns, (columnName) => {
          if (isReference && columnName === "investment_in") return null;

          const isInvestmentOrROI = columnName === "units_in" && isTpo;
          let refPriceIn;
          let estPriceIn;
          if (configCatSlice?.features.newInvestmentCalculation) {
            refPriceIn = referenceRow?.price_in_wo_taxes;
            estPriceIn = rowValues?.price_in_wo_taxes;
          } else {
            refPriceIn = referenceRow.price_in;
            estPriceIn = rowValues.price_in;
          }

          const investmentOrROI = isInvestmentOrROI
            ? rowValues.price < referenceRow.price
              ? getROI(
                  parseInt(rowValues.margin.toFixed(0), 10),
                  referenceRow.margin,
                  parseInt(
                    ((refPriceIn - estPriceIn) * rowValues.units_in).toFixed(0),
                    10
                  ),
                  {
                    strategy: configCatSlice?.features.roiStrategy,
                    unitsEsc: rowValues.units,
                    unitsRef: referenceRow.units,
                  }
                )
              : "N/A"
            : nFormatter(
                columnName === "investment_in"
                  ? parseInt(
                      ((refPriceIn - estPriceIn) * rowValues.units_in).toFixed(
                        0
                      ),
                      10
                    )
                  : rowValues[columnName]
              );

          const tableBodyPercentageInvestmentOrROI = isInvestmentOrROI
            ? null
            : rowValues[columnName] !== null &&
              `${Math.abs(
                (100 * (rowValues[columnName] - referenceRow[columnName])) /
                  referenceRow[columnName]
              ).toFixed(2)}%`;

          return (
            metadataSlice["custom-projection"][columnName][0]
              .visibility_type === 2 && (
              <div
                key={columnName}
                className={`${classes.rowCell} ${classes.numeric}`}
              >
                <Tooltip
                  title={getFormattedCellValue(columnName, investmentOrROI)}
                  placement="top-start"
                >
                  {!groupBy ? (
                    <Typography variant="body2" className={classes.cellValue}>
                      {columnName !== "investment_in"
                        ? getFormattedCellValue(columnName, investmentOrROI)
                        : (refPriceIn - estPriceIn) * rowValues.units_in > 0
                        ? nFormatter(
                            (refPriceIn - estPriceIn) * rowValues.units_in
                          )
                        : 0}
                    </Typography>
                  ) : (
                    <Typography variant="body2" className={classes.cellValue}>
                      {getFormattedCellValue(columnName, investmentOrROI)}
                    </Typography>
                  )}
                </Tooltip>
                {tableBodyPercentageInvestmentOrROI?.toString() !== "0.00%" &&
                  rowValues[columnName] !== null &&
                  rowValues[columnName] - referenceRow[columnName] !== 0 &&
                  columnName !== "investment_in" &&
                  !isReference && (
                    <Typography
                      variant="body2"
                      className={`${classes.cellPercentage}`}
                    >
                      {(!isTpo || columnName !== "units_in") && (
                        <BsArrowUp
                          size={13}
                          strokeWidth={1.5}
                          style={{
                            marginRight: "5px",
                            color: `${
                              columnName === "price"
                                ? "#a2a2a2"
                                : (100 *
                                    (rowValues[columnName] -
                                      referenceRow[columnName])) /
                                    referenceRow[columnName] >
                                  0
                                ? "#5AB793"
                                : "#FD6464"
                            }`,
                            transform: `rotateX(${
                              (100 *
                                (rowValues[columnName] -
                                  referenceRow[columnName])) /
                                referenceRow[columnName] >
                              0
                                ? "0deg"
                                : "180deg"
                            })`,
                          }}
                        />
                      )}
                      {tableBodyPercentageInvestmentOrROI}
                    </Typography>
                  )}
              </div>
            )
          );
        })}
        {isTpo &&
          isReference &&
          metadataSlice["custom-projection"].investment_in[0]
            .visibility_type === 2 && (
            <div
              key="investment_in"
              className={`${classes.rowCell} ${classes.numeric}`}
            >
              <Tooltip
                title={getFormattedCellValue(
                  "investment_in",
                  rowValues.investment_in
                )}
                placement="top-start"
              >
                <Typography variant="body2" className={classes.cellValue}>
                  {nFormatter(0)}
                </Typography>
              </Tooltip>
            </div>
          )}

        {/* 
        
          LOGIC OF INVESTMENT

        {!isReference &&
          !isTpo &&
          metadataSlice["custom-projection"].sales_in[0].visibility_type ===
            2 && (
            <div
              key="sales_in"
              className={`${classes.rowCell} ${classes.numeric}`}
            >
              <Typography variant="body2" className={classes.cellValue}>
                {getFormattedCellValue(
                  "investment",
                  (referenceRow.price - rowValues.price) * rowValues.units
                )}
                {getFormattedCellValue(
                  "sales_in",
                  nFormatter(rowValues.sales_in)
                )}
              </Typography>
            </div>
          )} */}
      </div>
      <Collapse in={index === expandedRow} timeout={0} unmountOnExit>
        <CardComponent
          title={
            <>
              <div className={classes.titleCardContainer}>
                <Typography variant="h6" className={classes.titleCard}>
                  Price Simulator
                </Typography>
                <div className={classes.textCardContainer}>
                  <Typography
                    variant="body2"
                    className={classes.textOwnElasticity}
                  >
                    Own Elasticity:
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{
                      paddingLeft: "5px",
                    }}
                    className={classes.textOwnElasticity}
                  >
                    {ownElasticity}
                  </Typography>
                </div>
              </div>
            </>
          }
          body={
            <div style={{ paddingBottom: 0, paddingTop: 0 }}>
              <Box className={classes.expansionDetailsContainer}>
                {currentTupleData ? (
                  <div className={classes.itemContainer}>
                    <ElasticityCurveChartComponent
                      scenarioEstimations={
                        scenariosSlice.selectedScenario.estimations
                      }
                      isReference={isReference}
                      selectedRow={rowValues}
                      referenceRow={referenceRow}
                      selectedRowByDate={null}
                      selectedReferenceRowByDate={undefined}
                      setIsMonthLevelChange={null}
                      groupBy={groupBy}
                      setSelectedPrice={setSelectedPrice}
                      isTradePromotionAnnual={isTradePromotionAnnual}
                      tupleEstimations={currentTupleData?.estimations}
                    />
                  </div>
                ) : (
                  <ScaleLoader size={150} color={primaryColor} loading />
                )}
              </Box>
              <Typography variant="body1" className={classes.textSlider}>
                {priceOutLabelByTenant} (
                {metadataSlice["custom-projection"].price[0].unit})
              </Typography>
            </div>
          }
          estimationCardContainer
        />
      </Collapse>
    </div>
  );
}
